import { useEffect, useRef } from 'react';

/**
 * Locks page scrolling while a modal/panel is open, WITHOUT causing the page to jump
 * to the top — and restores the exact original scroll position when it closes.
 *
 * THE BUG THIS FIXES: the actual "scrolls to top on open, stuck at top on close"
 * symptom turned out to be caused by `.focus()` calls elsewhere (moving focus onto a
 * modal's close button, or restoring focus to its trigger on close) not passing
 * `{ preventScroll: true }` — already fixed at each of those call sites. This hook's
 * job is narrower and simpler: just prevent the page from scrolling in the BACKGROUND
 * while a modal is open on top of it (standard "scroll lock" UX), and, defensively,
 * make sure the scroll position is still exactly where it was once the modal closes.
 *
 * THE FIX: capture the current `window.scrollY` on open, lock scrolling with plain
 * `overflow: hidden` on `<body>` (this alone does NOT reset scroll position in any
 * modern browser — it just prevents further scrolling while active), then on close
 * restore `overflow` and explicitly `window.scrollTo(0, scrollY)` as a defensive
 * no-op-in-the-common-case safety net, in case anything else nudged the scroll
 * position while the modal was open.
 *
 * NOTE: an earlier version of this hook also detached `<body>` from normal layout
 * with `position: fixed; top: -{scrollY}px; width: 100%`, a common "body scroll
 * lock" recipe. That was reverted — it caused the entire app to render as a solid
 * gray/blank screen behind the modal overlay instead of the dimmed page content,
 * because taking `<body>` out of normal flow this way conflicts with how this app's
 * layout/backgrounds are set up. Plain `overflow: hidden` avoids that side effect
 * entirely while still preventing background scroll.
 *
 * Usage — one line per modal, alongside whatever focus-trap/Escape-to-close effects
 * it already has:
 *
 *   useBodyScrollLock(isOpen);
 */
export function useBodyScrollLock(isOpen) {
  const scrollYRef = useRef(0);

  useEffect(() => {
    if (!isOpen) return undefined;

    scrollYRef.current = window.scrollY || window.pageYOffset || 0;

    const { body, documentElement: html } = document;
    const previousOverflow = body.style.overflow;
    const previousPaddingRight = body.style.paddingRight;
    // `<html>` also needs its overflow locked, not just `<body>`. Per the CSS spec,
    // body's `overflow: hidden` only controls the actual viewport/scrolling box if
    // `<html>` has no overflow of its own (fully `visible`). This app's `<html>`
    // already has `overflow-x: hidden` set globally (see App.css), which disqualifies
    // that propagation in some browsers — so body's lock silently does nothing there,
    // and `<html>` remains the real scrolling element: the native page scrollbar
    // stays on top and the mouse wheel keeps scrolling the whole page even while this
    // modal/panel is "open". Locking `<html>` directly as well makes this reliable
    // across browsers regardless of which element they treat as the scroll root.
    const previousHtmlOverflow = html.style.overflow;

    // Compensate for the scrollbar that `overflow: hidden` below is about to remove.
    // Without this, the page's content area widens by exactly the scrollbar's width
    // the instant the lock is applied (since that width is no longer reserved by a
    // visible scrollbar), shifting all page content sideways for the duration the
    // modal/panel is open. That sideways jump is what was reported as "the scrollbar
    // being covered/overridden by the main page bar" — the full-width header and page
    // content visibly jump/overlap at the moment a modal (or the provider-filters
    // sidebar, which had its own near-identical body.sidebar-open lock with the same
    // bug — see providerFilters/styles.css and providerFilters.jsx) opens or closes.
    // innerWidth - clientWidth is the standard, dependency-free way to measure the
    // actual rendered scrollbar width (0 on platforms with overlay scrollbars, e.g.
    // macOS/iOS/Android by default, where this is correctly a no-op).
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      const currentPaddingRight = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
    }

    return () => {
      body.style.overflow = previousOverflow;
      html.style.overflow = previousHtmlOverflow;
      body.style.paddingRight = previousPaddingRight;
      // Defensive: restore the exact pre-open scroll position in case anything
      // shifted it while the modal was open (e.g. a focus() call somewhere that
      // still lacks preventScroll). No-op in the common case.
      window.scrollTo(0, scrollYRef.current);
    };
  }, [isOpen]);
}

export default useBodyScrollLock;

